import { Checkbox } from "@base-ui/react/checkbox";
import { cn } from "@nt/ui/cn";
import { Field, FieldHint, FieldLabel } from "@nt/ui/field";
import { buttonVariants, checkboxRecipe, inputVariants } from "@nt/ui/recipes";
import { Check, ChevronDown } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Section } from "@/components/section";
import { Segmented } from "@/components/segmented";
import { SwitchRow } from "@/components/switch";
import type { TaxAnswers } from "@/lib/taxform";

export type Values = Record<string, string>;

type YesNoValue = "yes" | "no";

const YES_NO = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
] as const;

const asYesNo = (v: boolean | null): YesNoValue | null =>
  v === null ? null : v ? "yes" : "no";

/**
 * One question: its text on the left and a Yes / No `Segmented` on the right;
 * stacked under 640px. Starts unanswered (`value` null).
 */
export function YesNoRow({
  id,
  label,
  value,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  value: boolean | null;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div
      data-slot="yes-no-row"
      className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
    >
      <p id={id} className="text-sm text-foreground sm:max-w-[60ch]">
        {label}
      </p>
      <Segmented
        aria-label={label}
        value={asYesNo(value)}
        disabled={disabled}
        onChange={(v) => onChange(v === "yes")}
        options={YES_NO}
        className="shrink-0"
      />
    </div>
  );
}

function Text({
  id,
  label,
  values,
  set,
  hint,
  mono,
  optional,
  disabled,
}: {
  id: string;
  label: string;
  values: Values;
  set: (v: Values) => void;
  hint?: string;
  mono?: boolean;
  optional?: boolean;
  disabled?: boolean;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>
        {label}
        {optional ? " (optional)" : ""}
      </FieldLabel>
      <input
        id={id}
        required={!optional && !disabled}
        disabled={disabled}
        autoComplete="off"
        value={values[id] ?? ""}
        onChange={(e) => set({ ...values, [id]: e.target.value })}
        className={inputVariants({ mono: mono === true })}
      />
      {hint ? <FieldHint>{hint}</FieldHint> : null}
    </Field>
  );
}

/** A native `<select>` in the field look, with its own chevron. */
export function SelectField({
  id,
  label,
  value,
  onChange,
  children,
  hint,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="relative">
        <select
          id={id}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={inputVariants({ kind: "select" })}
        >
          {children}
        </select>
        <ChevronDown
          aria-hidden
          className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground"
        />
      </div>
      {hint ? <FieldHint>{hint}</FieldHint> : null}
    </Field>
  );
}

/** All three questions answered: the only state in which Continue is enabled. */
export function answersComplete(
  us: boolean | null,
  entity: boolean | null,
  work: boolean | null,
): boolean {
  return us !== null && entity !== null && work !== null;
}

export function Questionnaire({
  onDone,
  initial,
}: {
  onDone: (a: TaxAnswers) => void;
  /** Pre-filled answers (a partner going back from the form). */
  initial?: TaxAnswers | null;
}) {
  const [us, setUs] = useState<boolean | null>(initial?.usPerson ?? null);
  const [entity, setEntity] = useState<boolean | null>(
    initial ? initial.entity === "entity" : null,
  );
  const [work, setWork] = useState<boolean | null>(
    initial?.servicesInUs ?? null,
  );
  const complete = answersComplete(us, entity, work);
  return (
    <Section
      title="Which form do you need?"
      description="Three questions pick the right one. You confirm it before you sign."
    >
      <div className="flex flex-col divide-y divide-border">
        <YesNoRow
          id="tax-q-us"
          label="Are you a US citizen, a US resident, or a company organized in the US?"
          value={us}
          onChange={setUs}
        />
        <YesNoRow
          id="tax-q-entity"
          label="Are you signing up as a company (not as an individual)?"
          value={entity}
          onChange={setEntity}
        />
        <YesNoRow
          id="tax-q-work"
          label="Is any of your promotion work done while you are in the United States?"
          value={work}
          onChange={setWork}
        />
      </div>
      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center">
        <button
          type="button"
          disabled={!complete}
          className={buttonVariants({ size: "lg" })}
          onClick={() =>
            onDone({
              usPerson: us!,
              entity: entity ? "entity" : "individual",
              servicesInUs: work!,
            })
          }
        >
          Continue
        </button>
        {complete ? null : (
          <p className="text-compact text-muted-foreground">
            Answer all three questions to continue.
          </p>
        )}
      </div>
    </Section>
  );
}

/** The perjury statement: a Base UI checkbox (not a native one) with the full text as its label. */
function Attest({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 rounded-card bg-muted p-4">
      <Checkbox.Root
        id="attest"
        aria-labelledby="attest-label"
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onChange(next)}
        className={cn(checkboxRecipe.root, "mt-0.5")}
      >
        <Checkbox.Indicator className={checkboxRecipe.indicator}>
          <Check aria-hidden />
        </Checkbox.Indicator>
      </Checkbox.Root>
      <label
        id="attest-label"
        htmlFor="attest"
        className="text-sm text-foreground"
      >
        Under penalties of perjury, I declare that I have examined the
        information on this form and, to the best of my knowledge and belief, it
        is true, correct, and complete.
      </label>
    </div>
  );
}

export function Signature({
  values,
  set,
  disabled,
}: {
  values: Values;
  set: (v: Values) => void;
  disabled?: boolean;
}) {
  return (
    <>
      <Text
        id="sigName"
        label="Type your full name to sign"
        values={values}
        set={set}
        disabled={disabled}
      />
      <Attest
        checked={values.attest === "1"}
        disabled={disabled}
        onChange={(c) => set({ ...values, attest: c ? "1" : "" })}
      />
    </>
  );
}

const TIN_KINDS = [
  { value: "ssn", label: "SSN" },
  { value: "ein", label: "EIN" },
] as const;

export function W9Fields({
  v,
  setV,
}: {
  v: Values;
  setV: (v: Values) => void;
}) {
  return (
    <div className="grid gap-4">
      <Text
        id="name"
        label="Name (as shown on your tax return)"
        values={v}
        set={setV}
      />
      <Text
        id="businessName"
        label="Business name"
        values={v}
        set={setV}
        optional
      />
      <SelectField
        id="taxClassification"
        label="Federal tax classification"
        value={v.taxClassification ?? "individual"}
        onChange={(next) => setV({ ...v, taxClassification: next })}
      >
        <option value="individual">Individual / sole proprietor</option>
        <option value="c_corporation">C corporation</option>
        <option value="s_corporation">S corporation</option>
        <option value="partnership">Partnership</option>
        <option value="trust_estate">Trust / estate</option>
        <option value="llc">LLC</option>
        <option value="other">Other</option>
      </SelectField>
      {v.taxClassification === "llc" ? (
        <Text
          id="llcClassification"
          label="LLC tax classification (C, S or P)"
          values={v}
          set={setV}
        />
      ) : null}
      <Text id="address" label="Street address" values={v} set={setV} />
      <Text
        id="cityStateZip"
        label="City, state and ZIP code"
        values={v}
        set={setV}
        hint="Example: Austin, TX 78701"
      />
      <Field>
        <FieldLabel id="tinKind-label">
          Taxpayer identification number
        </FieldLabel>
        <Segmented
          aria-label="Taxpayer identification number type"
          value={v.tinKind === "ein" ? "ein" : "ssn"}
          onChange={(next) => setV({ ...v, tinKind: next })}
          options={TIN_KINDS}
        />
      </Field>
      <Text
        id="tin"
        label={v.tinKind === "ein" ? "EIN" : "SSN"}
        values={v}
        set={setV}
        mono
      />
    </div>
  );
}

export function W8benFields({
  v,
  setV,
}: {
  v: Values;
  setV: (v: Values) => void;
}) {
  const noTin = v.noForeignTin === "1";
  return (
    <div className="grid gap-4">
      <Text id="name" label="Full name" values={v} set={setV} />
      <Text
        id="citizenship"
        label="Country of citizenship (2 letters, like ES)"
        values={v}
        set={setV}
      />
      <Text
        id="street"
        label="Home street address"
        values={v}
        set={setV}
        hint="Not a P.O. box or in-care-of address."
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Text id="city" label="City" values={v} set={setV} />
        <Text
          id="state"
          label="State or province"
          values={v}
          set={setV}
          optional
        />
        <Text
          id="postalCode"
          label="Postal code"
          values={v}
          set={setV}
          optional
        />
        <Text
          id="country"
          label="Country of residence (2 letters)"
          values={v}
          set={setV}
        />
      </div>
      <SwitchRow
        id="noForeignTin"
        label="My country does not issue me a tax number"
        description="Turn this on only if your country of residence does not give individuals a tax number."
        checked={noTin}
        onCheckedChange={(on) => setV({ ...v, noForeignTin: on ? "1" : "" })}
      />
      {noTin ? null : (
        <Text
          id="foreignTin"
          label="Foreign tax number"
          values={v}
          set={setV}
          mono
          optional
        />
      )}
      <Text
        id="usTin"
        label="US SSN or ITIN"
        values={v}
        set={setV}
        mono
        optional
      />
      <Text
        id="dateOfBirth"
        label="Date of birth (YYYY-MM-DD)"
        values={v}
        set={setV}
      />
    </div>
  );
}
